import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    FaUser,
    FaEnvelope,
    FaPhone,
    FaCalendarAlt,
    FaMapMarkerAlt,
    FaFileContract,
    FaShieldAlt,
    FaPoundSign,
    FaFileAlt,
    FaDownload,
    FaExternalLinkAlt,
    FaIdCard,
    FaCreditCard,
    FaCheckCircle,
    FaTimesCircle,
    FaInfoCircle,
    FaTrashAlt,
    FaWrench,
    FaSyncAlt,
    FaCircleNotch,
    FaChevronDown,
} from 'react-icons/fa'
import styles from './styles.module.scss'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import { useAuthUserData } from '../../../authUserProvider'

interface LoanDetails {
    start_date: string | null
    length_of_contract: string | null
    total_amount_payable: string | number | null
    account_type: string | null
    lender_type: string | null
    agreement_status: string | null
    starting_balance: string | number | null
    commission: number | null
    total_days: number | null
    total_years: number | null
    stat_interest: number | null
    quantum_value: number | null
    monthly_repayment: string | number | null
}

interface EsignData {
    ip_address: string | null
    device: string | null
    browser: string | null
}

interface ClaimDocuments {
    [key: string]: string
}

interface Claim {
    funder_case_ref: string
    law_firm_case_reference: string
    ate_policy_id: string | null
    batch_group: string
    introducer: string
    api_transferred: string
    loan_value: number
    quantum_individual: number
    client_quantum_cumulative: number
    defendant: string
    cfa_date: string
    agreement_number: string
    incident_date: string
    ate_provider: string
    ate_inception_date: string
    client_status: string
    closed: string | null
    omnibus_id: string | null
    documents_pack_url: string | null
    raw_bundle_url: string | null
    base_claim: string | null
    pdf_claim: string | null
    origin: string | null
    experian_reference: string | null
    cancelled: string | null
    cancellation_date: string | null
    esign_data: EsignData
    loan_details: LoanDetails
    claim_documents: ClaimDocuments
}

interface ClientDetails {
    client_id: number
    title: string
    first_name: string
    last_name: string
    dob: string
    address_line_1: string
    address_line_2: string | null
    address_city: string
    address_county: string | null
    address_post_code: string
    address_country: string | null
    email_address: string
    phone_number: string
    ate_policy_number: string | null
    claims: Claim[]
}

type ClaimTab = 'overview' | 'financial' | 'loan' | 'dates' | 'status' | 'esign' | 'documents' | 'actions'

export interface FundingClientDetailsViewProps {
    /** When set (e.g. modal), use this id instead of the URL param */
    clientIdOverride?: string | number
    /** Modal / inline: use onClose instead of history after remove; adjust empty-state back */
    embedded?: boolean
    onClose?: () => void
    /**
     * Insurance Audit mode: loads via the whitelist-checked
     * /insurance-audit-client-details endpoint and hides all action
     * affordances (Actions tab, header actions, remove modal).
     */
    auditMode?: boolean
}

export const FundingClientDetailsView = ({
    clientIdOverride,
    embedded = false,
    onClose,
    auditMode = false,
}: FundingClientDetailsViewProps = {}) => {
    const params = useParams<{ clientId: string }>()
    const clientId =
        clientIdOverride != null && clientIdOverride !== '' ? String(clientIdOverride) : params.clientId
    const navigate = useNavigate()
    const authUser = useAuthUserData()
    const isScoreUser = authUser?.['custom:organisation'] === 'Score'
    // Read-only view: no batch removal, quantum reprocessing or doc regeneration.
    const hideActions = isScoreUser || auditMode
    const [clientDetails, setClientDetails] = useState<ClientDetails | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [expandedClaims, setExpandedClaims] = useState<Set<string>>(new Set())
    const [activeClaimTabs, setActiveClaimTabs] = useState<{ [key: string]: ClaimTab }>({})
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false)
    const [isRemoving, setIsRemoving] = useState<boolean>(false)
    const [regeneratingClaims, setRegeneratingClaims] = useState<Set<string>>(new Set())
    const [regeneratingDocs, setRegeneratingDocs] = useState<Set<string>>(new Set())
    const [isReprocessing, setIsReprocessing] = useState<boolean>(false)
    const [actionsDropdownOpen, setActionsDropdownOpen] = useState<boolean>(false)
    const actionsDropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target as Node)) {
                setActionsDropdownOpen(false)
            }
        }
        if (actionsDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [actionsDropdownOpen])

    const fetchClientDetails = useCallback(async () => {
        try {
            setLoading(true)

            const response = await api({
                url: auditMode ? '/insurance-audit-client-details' : '/funding-single-with-claims',
                data: {
                    clientId: clientId,
                },
            })

            if (response.data && response.data.success) {
                setClientDetails(response.data.client)
            } else {
                Report.failure('Error', response.data?.message || 'Failed to fetch client details', 'OKAY')
            }
        } catch (error: any) {
            console.error('Error fetching client details:', error)
            Report.failure('Error', error.message || 'Failed to fetch client details', 'OKAY')
        } finally {
            setLoading(false)
        }
    }, [clientId, auditMode])

    useEffect(() => {
        if (clientId) {
            fetchClientDetails()
        }
    }, [clientId, fetchClientDetails])

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'N/A'
        try {
            return new Date(dateString).toLocaleDateString('en-GB', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            })
        } catch {
            return dateString
        }
    }

    const formatCurrency = (value: number | null | undefined) => {
        if (value === null || value === undefined) return 'N/A'
        return `£${value.toLocaleString('en-GB', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`
    }

    // For values that might already be formatted as strings with £ symbol
    const displayCurrencyValue = (value: string | number | null | undefined) => {
        if (value === null || value === undefined || value === '') return 'N/A'
        // If it's a string that already contains £, return as-is
        if (typeof value === 'string') {
            if (value.includes('£')) return value
            // If it's a numeric string, try to format it
            const numValue = parseFloat(value.replace(/[^0-9.-]/g, ''))
            if (!isNaN(numValue)) {
                return formatCurrency(numValue)
            }
            return value
        }
        // If it's a number, format it
        return formatCurrency(value)
    }

    const toggleClaimExpansion = (claimRef: string) => {
        setExpandedClaims((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(claimRef)) {
                newSet.delete(claimRef)
            } else {
                newSet.add(claimRef)
                // Set default tab when expanding
                if (!activeClaimTabs[claimRef]) {
                    setActiveClaimTabs((prevTabs) => ({
                        ...prevTabs,
                        [claimRef]: 'overview',
                    }))
                }
            }
            return newSet
        })
    }

    const setClaimTab = (claimRef: string, tab: ClaimTab) => {
        setActiveClaimTabs((prev) => ({
            ...prev,
            [claimRef]: tab,
        }))
    }

    const getActiveTab = (claimRef: string): ClaimTab => {
        return activeClaimTabs[claimRef] || 'overview'
    }

    const formatAddress = (client: ClientDetails) => {
        const parts = [
            client.address_line_1,
            client.address_line_2,
            client.address_city,
            client.address_county,
            client.address_post_code,
            client.address_country,
        ].filter(Boolean)
        return parts.join(', ') || 'N/A'
    }

    const handleRemoveClient = async () => {
        if (!clientId) return
        setIsRemoving(true)
        try {
            const response = await api({
                url: '/funding-client-remove',
                data: { clientId },
            })
            if (response.data?.success) {
                Report.success('Client removed', response.data.message || 'Client has been removed from the batch.', 'OKAY')
                setShowRemoveModal(false)
                if (embedded && onClose) onClose()
                else navigate(-1)
            } else {
                Report.failure('Error', response.data?.message || 'Failed to remove client', 'OKAY')
            }
        } catch (error: any) {
            Report.failure('Error', error?.response?.data?.message || error.message || 'Failed to remove client', 'OKAY')
        } finally {
            setIsRemoving(false)
        }
    }

    const handleReprocessClient = async () => {
        if (!clientDetails) return
        setIsReprocessing(true)
        try {
            const response = await api({
                url: '/reprocess-client-quantum',
                data: { funder_client_id: clientDetails.client_id },
            })
            if (response.data?.success) {
                const s = response.data.summary
                const cumulative = response.data.quantumClientCumulative
                Report.success(
                    'Client Quantum Reprocessed',
                    `Updated ${s.updated} claim(s), skipped ${s.skipped}, failed ${s.failed}. New cumulative: ${
                        cumulative != null ? formatCurrency(cumulative) : 'n/a'
                    }`,
                    'OKAY',
                )
                await fetchClientDetails()
            } else {
                Report.failure('Reprocess Failed', response.data?.message || 'Failed to reprocess client', 'OKAY')
            }
        } catch (error: any) {
            Report.failure(
                'Reprocess Error',
                error?.response?.data?.message || error.message || 'Failed to reprocess client',
                'OKAY',
            )
        } finally {
            setIsReprocessing(false)
        }
    }

    const handleRegenerateBundle = async (claim: Claim) => {
        const ref = claim.funder_case_ref
        setRegeneratingClaims((prev) => new Set(prev).add(ref))
        try {
            const response = await api({
                url: '/regenerate-single-bundle',
                data: {
                    introducer_case_ref: claim.law_firm_case_reference,
                    introducer: claim.introducer,
                },
            })
            if (response.data?.success) {
                Report.success(
                    'Bundle Regenerated',
                    `Document bundle for ${claim.law_firm_case_reference} has been regenerated successfully. ${response.data.documentsIncluded?.length || 0} documents included.`,
                    'OKAY'
                )
                await fetchClientDetails()
            } else {
                Report.failure('Regeneration Failed', response.data?.message || 'Failed to regenerate bundle', 'OKAY')
            }
        } catch (error: any) {
            Report.failure('Regeneration Error', error?.response?.data?.message || error.message || 'Failed to regenerate bundle', 'OKAY')
        } finally {
            setRegeneratingClaims((prev) => {
                const next = new Set(prev)
                next.delete(ref)
                return next
            })
        }
    }

    const REGENERABLE_DOCUMENTS = [
        { key: 'cfa', label: 'CFA (Conditional Fee Agreement)' },
        { key: 'loa', label: 'LOA (Letter of Authority)' },
        { key: 'loc', label: 'LOC (Letter of Complaint)' },
        { key: 'aof', label: 'AOF (Assignment of Funds)' },
        { key: 'trForensicsReport', label: 'TR Forensic Report' },
        { key: 'trInstructionLetter', label: 'TR Instruction Letter' },
        { key: 'esignCertificate', label: 'E-Sign Certificate' },
    ]

    const handleRegenerateDocument = async (claim: Claim, documentKey: string, label: string) => {
        const compositeKey = `${claim.funder_case_ref}:${documentKey}`
        setRegeneratingDocs((prev) => new Set(prev).add(compositeKey))
        try {
            const response = await api({
                url: '/regenerate-single-document',
                data: {
                    introducer_case_ref: claim.law_firm_case_reference,
                    introducer: claim.introducer,
                    documentKey,
                },
            })
            if (response.data?.success) {
                Report.success('Document Regenerated', `${label} has been regenerated successfully.`, 'OKAY')
                await fetchClientDetails()
            } else {
                Report.failure('Regeneration Failed', response.data?.message || `Failed to regenerate ${label}`, 'OKAY')
            }
        } catch (error: any) {
            Report.failure('Regeneration Error', error?.response?.data?.message || error.message || `Failed to regenerate ${label}`, 'OKAY')
        } finally {
            setRegeneratingDocs((prev) => {
                const next = new Set(prev)
                next.delete(compositeKey)
                return next
            })
        }
    }

    const hasEsignData = (claim: Claim) => {
        return claim.esign_data && (claim.esign_data.ip_address || claim.esign_data.device || claim.esign_data.browser)
    }

    const hasDocuments = (claim: Claim) => {
        return (claim.claim_documents && Object.keys(claim.claim_documents).length > 0) || claim.documents_pack_url || claim.raw_bundle_url
    }

    const getAvailableTabs = (claim: Claim) => {
        const tabs: { id: ClaimTab; label: string; icon: JSX.Element }[] = [
            { id: 'overview', label: 'Overview', icon: <FaFileContract /> },
            { id: 'financial', label: 'Financial', icon: <FaPoundSign /> },
        ]

        if (claim.loan_details) {
            tabs.push({ id: 'loan', label: 'Loan Details', icon: <FaCreditCard /> })
        }

        tabs.push({ id: 'dates', label: 'Dates & ATE', icon: <FaCalendarAlt /> })
        tabs.push({ id: 'status', label: 'Status', icon: <FaInfoCircle /> })

        if (hasEsignData(claim)) {
            tabs.push({ id: 'esign', label: 'E-Sign', icon: <FaIdCard /> })
        }

        if (hasDocuments(claim)) {
            tabs.push({ id: 'documents', label: 'Documents', icon: <FaFileAlt /> })
        }

        if (!hideActions) {
            tabs.push({ id: 'actions', label: 'Actions', icon: <FaWrench /> })
        }

        return tabs
    }

    if (loading) {
        return (
            <div className={styles.detailsContainer}>
                <div className={styles.loadingState}>
                    <div className={styles.spinner}></div>
                    <p>Loading client details...</p>
                </div>
            </div>
        )
    }

    if (!clientDetails) {
        return (
            <div className={styles.detailsContainer}>
                <div className={styles.noResults}>
                    <p>Client details not found.</p>
                    <button
                        className={styles.backButton}
                        onClick={() => (embedded && onClose ? onClose() : navigate(-1))}
                    >
                        Go Back
                    </button>
                </div>
            </div>
        )
    }

    const renderClaimTabContent = (claim: Claim, activeTab: ClaimTab) => {
        switch (activeTab) {
            case 'overview':
                return (
                    <div className={styles.tabContent}>
                        <div className={styles.claimGrid}>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Funder Case Ref</span>
                                <span className={styles.claimItemValue}>{claim.funder_case_ref || 'N/A'}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Law Firm Ref</span>
                                <span className={styles.claimItemValue}>{claim.law_firm_case_reference || 'N/A'}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Defendant</span>
                                <span className={styles.claimItemValue}>{claim.defendant || 'N/A'}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Introducer</span>
                                <span className={styles.claimItemValue}>{claim.introducer || 'N/A'}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Batch Group</span>
                                <span className={styles.claimItemValue}>{claim.batch_group || 'N/A'}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Origin</span>
                                <span className={styles.claimItemValue}>{claim.origin || 'N/A'}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Base Claim</span>
                                <span className={styles.claimItemValue}>{claim.base_claim || 'N/A'}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>PDF Claim</span>
                                <span className={styles.claimItemValue}>{claim.pdf_claim || 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                )

            case 'financial':
                return (
                    <div className={styles.tabContent}>
                        <div className={styles.claimGrid}>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Quantum Individual</span>
                                <span className={styles.claimItemValue}>
                                    {formatCurrency(claim.quantum_individual)}
                                </span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Quantum Cumulative</span>
                                <span className={styles.claimItemValue}>
                                    {formatCurrency(claim.client_quantum_cumulative)}
                                </span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Loan Value</span>
                                <span className={styles.claimItemValue}>{formatCurrency(claim.loan_value)}</span>
                            </div>
                        </div>
                    </div>
                )

            case 'loan':
                if (!claim.loan_details) return null
                return (
                    <div className={styles.tabContent}>
                        <div className={styles.claimGrid}>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Start Date</span>
                                <span className={styles.claimItemValue}>
                                    {formatDate(claim.loan_details.start_date)}
                                </span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Contract Length</span>
                                <span className={styles.claimItemValue}>
                                    {claim.loan_details.length_of_contract || 'N/A'}
                                </span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Total Amount Payable</span>
                                <span className={styles.claimItemValue}>
                                    {displayCurrencyValue(claim.loan_details.total_amount_payable)}
                                </span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Starting Balance</span>
                                <span className={styles.claimItemValue}>
                                    {displayCurrencyValue(claim.loan_details.starting_balance)}
                                </span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Monthly Repayment</span>
                                <span className={styles.claimItemValue}>
                                    {displayCurrencyValue(claim.loan_details.monthly_repayment)}
                                </span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Account Type</span>
                                <span className={styles.claimItemValue}>
                                    {claim.loan_details.account_type || 'N/A'}
                                </span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Lender Type</span>
                                <span className={styles.claimItemValue}>{claim.loan_details.lender_type || 'N/A'}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Agreement Number</span>
                                <span className={styles.claimItemValue}>
                                    {claim.agreement_number || 'N/A'}
                                </span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Agreement Status</span>
                                <span className={styles.claimItemValue}>
                                    {claim.loan_details.agreement_status || 'N/A'}
                                </span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Commission</span>
                                <span className={styles.claimItemValue}>
                                    {formatCurrency(claim.loan_details.commission)}
                                </span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Stat Interest</span>
                                <span className={styles.claimItemValue}>
                                    {formatCurrency(claim.loan_details.stat_interest)}
                                </span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Quantum Value</span>
                                <span className={styles.claimItemValue}>
                                    {formatCurrency(claim.loan_details.quantum_value)}
                                </span>
                            </div>
                        </div>
                    </div>
                )

            case 'dates':
                return (
                    <div className={styles.tabContent}>
                        <div className={styles.claimGrid}>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>CFA Date</span>
                                <span className={styles.claimItemValue}>{formatDate(claim.cfa_date)}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Incident Date</span>
                                <span className={styles.claimItemValue}>{formatDate(claim.incident_date)}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>ATE Provider</span>
                                <span className={styles.claimItemValue}>{claim.ate_provider || 'N/A'}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>ATE Inception Date</span>
                                <span className={styles.claimItemValue}>{formatDate(claim.ate_inception_date)}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>ATE Certificate Number</span>
                                <span className={styles.claimItemValue}>{claim.ate_policy_id || 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                )

            case 'status':
                return (
                    <div className={styles.tabContent}>
                        <div className={styles.claimGrid}>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Client Status</span>
                                <span className={styles.claimItemValue}>{claim.client_status || 'N/A'}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>API Transferred</span>
                                <span className={styles.claimItemValue}>
                                    {claim.api_transferred?.toLowerCase() === 'yes' ? (
                                        <span className={styles.statusBadgeActive}>
                                            <FaCheckCircle /> Yes
                                        </span>
                                    ) : (
                                        <span className={styles.statusBadgeInactive}>
                                            <FaTimesCircle /> No
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Closed</span>
                                <span className={styles.claimItemValue}>{claim.closed || 'No'}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Cancelled</span>
                                <span className={styles.claimItemValue}>{claim.cancelled || 'No'}</span>
                            </div>
                            {claim.cancellation_date && (
                                <div className={styles.claimItem}>
                                    <span className={styles.claimLabel}>Cancellation Date</span>
                                    <span className={styles.claimItemValue}>{formatDate(claim.cancellation_date)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )

            case 'esign':
                if (!hasEsignData(claim)) return null
                return (
                    <div className={styles.tabContent}>
                        <div className={styles.claimGrid}>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>IP Address</span>
                                <span className={styles.claimItemValue}>{claim.esign_data.ip_address || 'N/A'}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Device</span>
                                <span className={styles.claimItemValue}>{claim.esign_data.device || 'N/A'}</span>
                            </div>
                            <div className={styles.claimItem}>
                                <span className={styles.claimLabel}>Browser</span>
                                <span className={styles.claimItemValue}>{claim.esign_data.browser || 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                )

            case 'documents':
                return (
                    <div className={styles.tabContent}>
                        {!isScoreUser && claim.claim_documents && Object.keys(claim.claim_documents).length > 0 && (
                            <div className={styles.documentsSection}>
                                <h5 className={styles.documentsSubtitle}>Claim Documents</h5>
                                <div className={styles.documentsGrid}>
                                    {Object.entries(claim.claim_documents).map(([docName, docUrl]) => (
                                        <a
                                            key={docName}
                                            href={docUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.documentLink}
                                        >
                                            <FaFileAlt className={styles.documentIcon} />
                                            <span className={styles.documentName}>{docName}</span>
                                            <FaExternalLinkAlt className={styles.externalIcon} />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                        {(claim.documents_pack_url || (!isScoreUser && claim.raw_bundle_url)) && (
                            <div className={styles.documentsPackSection}>
                                <h5 className={styles.documentsSubtitle}>Documents Pack</h5>
                                {claim.documents_pack_url && (
                                    <a
                                        href={claim.documents_pack_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.downloadPackButton}
                                    >
                                        <FaDownload />
                                        Download Documents Pack
                                    </a>
                                )}
                                {!isScoreUser && claim.raw_bundle_url && (
                                    <a
                                        href={claim.raw_bundle_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={styles.downloadPackButton}
                                        style={{ marginLeft: claim.documents_pack_url ? '8px' : '0' }}
                                    >
                                        <FaFileAlt />
                                        Download Raw PDF
                                    </a>
                                )}
                            </div>
                        )}
                    </div>
                )

            case 'actions':
                return (
                    <div className={styles.tabContent}>
                        <div className={styles.actionsSection}>
                            <div className={styles.actionCard}>
                                <div className={styles.actionCardInfo}>
                                    <h5 className={styles.actionCardTitle}>Regenerate Document Bundle</h5>
                                    <p className={styles.actionCardDescription}>
                                        Regenerate the document bundle for this claim using the latest data from the database.
                                        The existing document link will be preserved.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className={styles.actionButton}
                                    onClick={() => handleRegenerateBundle(claim)}
                                    disabled={regeneratingClaims.has(claim.funder_case_ref)}
                                >
                                    {regeneratingClaims.has(claim.funder_case_ref) ? (
                                        <><FaCircleNotch className={styles.spinIcon} /> Regenerating...</>
                                    ) : (
                                        <><FaSyncAlt /> Regenerate Bundle</>
                                    )}
                                </button>
                            </div>

                            <h5 className={styles.docRegenSectionTitle}>Regenerate Individual Documents</h5>
                            <div className={styles.docRegenGrid}>
                                {REGENERABLE_DOCUMENTS.map((doc) => {
                                    const compositeKey = `${claim.funder_case_ref}:${doc.key}`
                                    const isRegenerating = regeneratingDocs.has(compositeKey)
                                    return (
                                        <button
                                            key={doc.key}
                                            type="button"
                                            className={styles.docRegenButton}
                                            onClick={() => handleRegenerateDocument(claim, doc.key, doc.label)}
                                            disabled={isRegenerating}
                                        >
                                            {isRegenerating ? (
                                                <><FaCircleNotch className={styles.spinIcon} /> Regenerating...</>
                                            ) : (
                                                <><FaFileAlt /> {doc.label}</>
                                            )}
                                        </button>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                )

            default:
                return null
        }
    }

    return (
        <div className={styles.detailsContainer}>
            {/* Client Header */}
            <div className={styles.clientHeader}>
                <div className={styles.clientInfo}>
                    <div className={styles.clientAvatar}>
                        <FaUser />
                    </div>
                    <div className={styles.clientNameSection}>
                        <h1 className={styles.clientName}>
                            {clientDetails.title} {clientDetails.first_name} {clientDetails.last_name}
                        </h1>
                        <p className={styles.clientId}>Client ID: {clientDetails.client_id}</p>
                    </div>
                </div>
                <div className={styles.claimsSummary}>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Total Claims</span>
                        <span className={styles.summaryValue}>{clientDetails.claims?.length || 0}</span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Total Value</span>
                        <span className={styles.summaryValue}>
                            {formatCurrency(
                                clientDetails.claims?.reduce((sum, claim) => sum + (claim.quantum_individual || 0), 0),
                            )}
                        </span>
                    </div>
                    {!hideActions && (
                        <div className={styles.headerActions} ref={actionsDropdownRef}>
                            <div
                                className={`${styles.actionsDropdown} ${
                                    actionsDropdownOpen ? styles.actionsDropdownOpen : ''
                                }`}
                            >
                                <button
                                    type="button"
                                    className={styles.actionsDropdownTrigger}
                                    onClick={() => setActionsDropdownOpen((o) => !o)}
                                    aria-expanded={actionsDropdownOpen}
                                    aria-haspopup="true"
                                >
                                    Actions
                                    <FaChevronDown className={styles.actionsDropdownChevron} />
                                </button>
                                {actionsDropdownOpen && (
                                    <div className={styles.actionsDropdownMenu}>
                                        <button
                                            type="button"
                                            className={styles.actionsDropdownItem}
                                            onClick={() => {
                                                setActionsDropdownOpen(false)
                                                handleReprocessClient()
                                            }}
                                            disabled={isReprocessing}
                                        >
                                            <span className={styles.actionsDropdownItemLabel}>
                                                {isReprocessing ? (
                                                    <>
                                                        <FaCircleNotch className={styles.spinIcon} />
                                                        Reprocessing…
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaSyncAlt />
                                                        Reprocess Quantum
                                                    </>
                                                )}
                                            </span>
                                            <span className={styles.actionsDropdownItemDesc}>
                                                Re-pull each claim&apos;s starting balance from the introducer leads
                                                table and recompute quantum_individual and quantum_client_cumulative
                                                for this client.
                                            </span>
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.actionsDropdownItem} ${styles.actionsDropdownItemDanger}`}
                                            onClick={() => {
                                                setActionsDropdownOpen(false)
                                                setShowRemoveModal(true)
                                            }}
                                        >
                                            <span className={styles.actionsDropdownItemLabel}>
                                                <FaTrashAlt />
                                                Remove Client
                                            </span>
                                            <span className={styles.actionsDropdownItemDesc}>
                                                Remove this client (and all of their claims) from the funding batch.
                                                Cannot be undone from here.
                                            </span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Client Details Card */}
            <div className={styles.detailsCard}>
                <div className={styles.sectionHeader}>
                    <FaUser className={styles.sectionIcon} />
                    <h2>Personal Details</h2>
                </div>
                <div className={styles.detailsGrid}>
                    <div className={styles.detailItem}>
                        <FaIdCard className={styles.detailIcon} />
                        <div className={styles.detailContent}>
                            <span className={styles.detailLabel}>Full Name</span>
                            <span className={styles.detailValue}>
                                {clientDetails.title} {clientDetails.first_name} {clientDetails.last_name}
                            </span>
                        </div>
                    </div>
                    <div className={styles.detailItem}>
                        <FaCalendarAlt className={styles.detailIcon} />
                        <div className={styles.detailContent}>
                            <span className={styles.detailLabel}>Date of Birth</span>
                            <span className={styles.detailValue}>{formatDate(clientDetails.dob)}</span>
                        </div>
                    </div>
                    <div className={styles.detailItem}>
                        <FaEnvelope className={styles.detailIcon} />
                        <div className={styles.detailContent}>
                            <span className={styles.detailLabel}>Email Address</span>
                            <span className={styles.detailValue}>{clientDetails.email_address || 'N/A'}</span>
                        </div>
                    </div>
                    <div className={styles.detailItem}>
                        <FaPhone className={styles.detailIcon} />
                        <div className={styles.detailContent}>
                            <span className={styles.detailLabel}>Phone Number</span>
                            <span className={styles.detailValue}>{clientDetails.phone_number || 'N/A'}</span>
                        </div>
                    </div>
                    <div className={styles.detailItem}>
                        <FaMapMarkerAlt className={styles.detailIcon} />
                        <div className={styles.detailContent}>
                            <span className={styles.detailLabel}>Address</span>
                            <span className={styles.detailValue}>{formatAddress(clientDetails)}</span>
                        </div>
                    </div>
                    <div className={styles.detailItem}>
                        <FaShieldAlt className={styles.detailIcon} />
                        <div className={styles.detailContent}>
                            <span className={styles.detailLabel}>ATE Policy Number</span>
                            <span className={styles.detailValue}>{clientDetails.ate_policy_number || 'N/A'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Claims Section */}
            <div className={styles.claimsSection}>
                <div className={styles.sectionHeader}>
                    <FaFileContract className={styles.sectionIcon} />
                    <h2>Claims ({clientDetails.claims?.length || 0})</h2>
                </div>

                {clientDetails.claims && clientDetails.claims.length > 0 ? (
                    <div className={styles.claimsList}>
                        {[...clientDetails.claims].sort((a, b) => {
                            const suffix = (ref: string) => {
                                const parts = ref?.split('.') || []
                                return parts.length > 1 ? parseInt(parts[parts.length - 1], 10) || 0 : 0
                            }
                            return suffix(a.funder_case_ref) - suffix(b.funder_case_ref)
                        }).map((claim, index) => (
                            <div key={claim.funder_case_ref || index} className={styles.claimCard}>
                                <div
                                    className={styles.claimHeader}
                                    onClick={() => toggleClaimExpansion(claim.funder_case_ref)}
                                >
                                    <div className={styles.claimHeaderLeft}>
                                        <span className={styles.claimNumber}>Claim {index + 1}</span>
                                        <span className={styles.claimRef}>{claim.funder_case_ref}</span>
                                        <span
                                            className={`${styles.claimStatus} ${
                                                claim.client_status?.toLowerCase() === 'active'
                                                    ? styles.statusActive
                                                    : styles.statusInactive
                                            }`}
                                        >
                                            {claim.client_status || 'Unknown'}
                                        </span>
                                    </div>
                                    <div className={styles.claimHeaderRight}>
                                        <span className={styles.claimValue}>
                                            {formatCurrency(claim.quantum_individual)}
                                        </span>
                                        <span
                                            className={`${styles.expandIcon} ${
                                                expandedClaims.has(claim.funder_case_ref) ? styles.expanded : ''
                                            }`}
                                        >
                                            ▼
                                        </span>
                                    </div>
                                </div>

                                {expandedClaims.has(claim.funder_case_ref) && (
                                    <div className={styles.claimDetails}>
                                        {/* Tab Navigation */}
                                        <div className={styles.tabNavigation}>
                                            {getAvailableTabs(claim).map((tab) => (
                                                <button
                                                    key={tab.id}
                                                    type="button"
                                                    className={`${styles.tabButton} ${
                                                        getActiveTab(claim.funder_case_ref) === tab.id
                                                            ? styles.tabActive
                                                            : ''
                                                    }`}
                                                    onClick={() => setClaimTab(claim.funder_case_ref, tab.id)}
                                                >
                                                    {tab.icon}
                                                    <span className={styles.tabLabel}>{tab.label}</span>
                                                </button>
                                            ))}
                                        </div>

                                        {/* Tab Content */}
                                        {renderClaimTabContent(claim, getActiveTab(claim.funder_case_ref))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={styles.noClaims}>
                        <p>No claims found for this client.</p>
                    </div>
                )}
            </div>

            {!hideActions && showRemoveModal && (
                <div className={styles.modalOverlay} onClick={() => !isRemoving && setShowRemoveModal(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Remove client from batch</h3>
                            <button
                                type="button"
                                className={styles.modalCloseButton}
                                onClick={() => setShowRemoveModal(false)}
                                aria-label="Close modal"
                                disabled={isRemoving}
                            >
                                &times;
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <p>
                                You are about to remove <strong>{clientDetails.title} {clientDetails.first_name} {clientDetails.last_name}</strong> (ID: {clientDetails.client_id}) and all {clientDetails.claims?.length || 0} associated claim(s) from the funder monitoring system.
                            </p>
                            <div className={styles.modalWarning}>
                                This will permanently delete the client record, all claim records, and all reference records from the database. This action cannot be undone.
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={() => setShowRemoveModal(false)}
                                disabled={isRemoving}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={styles.confirmDeleteButton}
                                onClick={handleRemoveClient}
                                disabled={isRemoving}
                            >
                                {isRemoving ? 'Removing...' : 'Remove Client'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
